import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Instagram, Star } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Welcome to Team Bailey — check your inbox soon.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer id="team-bailey" data-testid="site-footer" className="border-t border-sand bg-creamdark/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="relative grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <Star className="twinkle absolute -top-6 -left-4 text-amberbrand" size={30} fill="currentColor" style={{ animationDelay: "0s" }} />
            <Star className="twinkle absolute top-16 left-[46%] text-terracotta" size={20} fill="currentColor" style={{ animationDelay: "0.8s" }} />
            <Star className="twinkle absolute -bottom-2 left-[38%] text-sage" size={24} fill="currentColor" style={{ animationDelay: "1.6s" }} />
            <Star className="twinkle absolute top-1/2 right-2 text-amberbrand" size={26} fill="currentColor" style={{ animationDelay: "2.2s" }} />
            <svg className="absolute top-0 right-[30%] animate-float-slow" width="44" height="44" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="20" fill="#FFE9A8" stroke="#31435C" strokeWidth="2.5" />
              <circle cx="15.5" cy="18" r="2.5" fill="#31435C" />
              <circle cx="28.5" cy="18" r="2.5" fill="#31435C" />
              <path d="M14 26 q8 8 16 0" stroke="#31435C" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            </svg>
            <svg className="absolute -bottom-8 right-[8%] animate-float-slower hidden sm:block" width="36" height="36" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="20" fill="#BFE0F7" stroke="#31435C" strokeWidth="2.5" />
              <circle cx="15.5" cy="18" r="2.5" fill="#31435C" />
              <circle cx="28.5" cy="18" r="2.5" fill="#31435C" />
              <path d="M14 26 q8 8 16 0" stroke="#31435C" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            </svg>
            <svg className="absolute top-24 -left-8 animate-float-slow hidden lg:block" width="34" height="34" viewBox="0 0 44 44" style={{ animationDelay: "1.2s" }}>
              <circle cx="22" cy="22" r="20" fill="#A8D5A2" stroke="#31435C" strokeWidth="2.5" />
              <circle cx="15.5" cy="18" r="2.5" fill="#31435C" />
              <circle cx="28.5" cy="18" r="2.5" fill="#31435C" />
              <path d="M14 26 q8 8 16 0" stroke="#31435C" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-walnut mb-4">
              Join <span className="text-terracotta">Team Bailey</span>
            </h2>
            <p className="text-sm sm:text-base text-walnutsoft leading-relaxed max-w-md">
              New books, free printable activities and school visit dates — straight to
              your inbox. No noise, just the good stuff.
            </p>
          </div>
          <form data-testid="newsletter-form" onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 lg:justify-end lg:pt-3">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="w-full sm:w-80 rounded-full border border-sand bg-white/80 px-6 py-4 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition"
            />
            <button
              data-testid="newsletter-submit-button"
              type="submit"
              disabled={sending}
              className="group rounded-full bg-walnut text-cream px-7 py-4 font-display font-bold text-sm flex items-center justify-center gap-2 hover:bg-terracotta transition-colors disabled:opacity-60"
            >
              {sending ? "Joining…" : "Subscribe"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 pt-10 border-t border-sand">
          <div className="flex items-center gap-2.5">
            <img src="/images/lwb-logo.png" alt="Learn With Bailey logo" className="h-10 w-auto" />
            <div>
              <p className="font-display font-bold text-sm text-walnut">Learn With Bailey</p>
              <p className="text-xs text-walnutmuted">Written by Damali Allison · Illustrated by Meni Etim</p>
            </div>
          </div>
          <nav className="flex flex-wrap gap-x-8 gap-y-3">
            <Link to="/books" data-testid="footer-link-books" className="text-sm text-walnutsoft hover:text-terracotta transition-colors">Books</Link>
            <Link to="/author" data-testid="footer-link-author" className="text-sm text-walnutsoft hover:text-terracotta transition-colors">About Damali</Link>
            <a href="https://www.instagram.com/baileysadventurebooks/" target="_blank" rel="noopener noreferrer" data-testid="footer-link-instagram" className="inline-flex items-center gap-1.5 text-sm text-walnutsoft hover:text-terracotta transition-colors">
              <Instagram size={15} /> @baileysadventurebooks
            </a>
            <a href="https://www.learnwithbailey.org/free-resources" target="_blank" rel="noopener noreferrer" data-testid="footer-link-free-resources" className="text-sm text-walnutsoft hover:text-terracotta transition-colors">Free activities</a>
            <a href="https://www.amazon.co.uk/s?k=damali+allison" target="_blank" rel="noopener noreferrer" data-testid="footer-link-amazon" className="text-sm text-walnutsoft hover:text-terracotta transition-colors">Amazon UK</a>
            <a href="https://www.learnwithbailey.org/contact" target="_blank" rel="noopener noreferrer" data-testid="footer-link-contact" className="text-sm text-walnutsoft hover:text-terracotta transition-colors">Contact</a>
          </nav>
          <p className="text-xs text-walnutmuted">© {new Date().getFullYear()} Learn With Bailey. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
